import { useState, useEffect } from 'react';
import { Link } from 'react-scroll';
import { motion, AnimatePresence, useScroll, useSpring } from 'framer-motion';
import AVATAR from '../../images/ujjal-zaman-nyc.jpg';

const NAV = [
  { to: 'about',      label: 'About'      },
  { to: 'experience', label: 'Experience' },
  { to: 'projects',   label: 'Work'       },
  { to: 'education',  label: 'Education'  },
  { to: 'contact',    label: 'Contact'    },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [active,   setActive]   = useState('');
  const [imgErr,   setImgErr]   = useState(false);
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 200, damping: 30 });

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', h, { passive: true });
    return () => window.removeEventListener('scroll', h);
  }, []);

  return (
    <>
      <motion.div
        style={{ scaleX, originX: 0, background: 'linear-gradient(90deg,#6366f1,#a78bfa,#22d3ee)' }}
        className="fixed top-0 left-0 right-0 h-[1.5px] z-[60]"
      />

      <motion.header
        initial={{ y: -72, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.65, ease: [0.16, 1, 0.3, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-400 ${
          scrolled ? 'bg-ink-950/70 backdrop-blur-2xl border-b border-white/[0.04]' : ''
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center gap-8">

          <Link
            to="hero" smooth duration={600}
            className="group cursor-pointer flex items-center gap-2.5 flex-shrink-0"
          >
            <div className="relative w-8 h-8">
              <div className="absolute inset-[-1.5px] rounded-full overflow-hidden">
                <div
                  className="spin-conic absolute"
                  style={{
                    inset: '-100%',
                    background: 'conic-gradient(from 0deg at 50% 50%, #6366f1 0%, #a78bfa 33%, #22d3ee 66%, #6366f1 100%)',
                  }}
                />
              </div>
              <div className="relative w-full h-full rounded-full overflow-hidden bg-ink-800 border border-ink-950">
                {!imgErr ? (
                  <img
                    src={AVATAR}
                    alt="Ujjal Zaman"
                    onError={() => setImgErr(true)}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-indigo-600 to-violet-700">
                    <span className="font-display font-extrabold text-[10px] text-white">UZ</span>
                  </div>
                )}
              </div>
            </div>

            <div className="hidden sm:block leading-none">
              <span className="font-display font-bold text-sm text-white">Ujjal</span>
              <span className="font-display font-bold text-sm gradient-text ml-1">Zaman</span>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-7 flex-1 justify-center">
            {NAV.map(({ to, label }) => (
              <Link
                key={to}
                to={to} smooth duration={600} offset={-64}
                spy onSetActive={() => setActive(to)}
                className="relative text-sm font-medium cursor-pointer transition-colors duration-200
                           text-zinc-500 hover:text-white group"
              >
                {label}
                <motion.span
                  className="absolute -bottom-0.5 left-0 h-[1.5px] rounded-full bg-indigo-500"
                  animate={{ width: active === to ? '100%' : '0%' }}
                  transition={{ duration: 0.25 }}
                />
                <span className="absolute -bottom-0.5 left-0 right-0 h-[1.5px] bg-zinc-700 opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-full" />
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3 flex-shrink-0 ml-auto md:ml-0">
            <a
              href="https://drive.google.com/file/d/1GEYvUD7g0UEldrNOwuPBe8oGIH16bJir/view"
              target="_blank" rel="noopener noreferrer"
              className="hidden md:inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white
                rounded-xl bg-indigo-600 hover:bg-indigo-500 shadow-glow-sm hover:shadow-glow-md
                transition-all duration-200"
            >
              Resume ↗
            </a>

            <button
              type="button"
              onClick={() => setMenuOpen(o => !o)}
              aria-label="Toggle menu"
              className="md:hidden p-2 rounded-xl hover:bg-white/[0.05] text-zinc-400 transition-colors"
            >
              <div className="w-5 h-3.5 flex flex-col justify-between">
                <motion.span animate={menuOpen ? { rotate: 45,  y:  6 } : { rotate: 0, y: 0 }} className="block h-px bg-current origin-center" />
                <motion.span animate={menuOpen ? { opacity: 0 }          : { opacity: 1 }}      className="block h-px bg-current" />
                <motion.span animate={menuOpen ? { rotate: -45, y: -8 } : { rotate: 0, y: 0 }} className="block h-px bg-current origin-center" />
              </div>
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 bg-ink-950/97 backdrop-blur-3xl flex flex-col pt-16 md:hidden"
          >
            <nav className="flex flex-col items-center justify-center flex-1 gap-8 pb-24">
              {NAV.map(({ to, label }, i) => (
                <motion.div
                  key={to}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.07 }}
                >
                  <Link
                    to={to} smooth duration={600} offset={-64}
                    onClick={() => setMenuOpen(false)}
                    className="font-display font-extrabold text-4xl text-zinc-700 hover:text-white transition-colors cursor-pointer"
                  >
                    {label}
                  </Link>
                </motion.div>
              ))}
              <motion.a
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: NAV.length * 0.07 }}
                href="https://drive.google.com/file/d/1GEYvUD7g0UEldrNOwuPBe8oGIH16bJir/view"
                target="_blank" rel="noopener noreferrer"
                className="mt-2 px-8 py-3.5 font-semibold text-white bg-indigo-600 rounded-2xl hover:bg-indigo-500 transition-colors"
              >
                Resume ↗
              </motion.a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}